// Spec: specs/stats/questions/functional_requirements.md §6.10

import { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { Button } from 'primereact/button'

import { CAUSE, markFor, REMEDY, remedyFor } from '@aytm/app/stats/page/question-views/components/data-run/lib/causes'
import { calcCopy, RUN_LABEL, RUNNING_LABEL } from '@aytm/app/stats/page/question-views/components/data-run/lib/copy'
import Field from '@aytm/app/stats/page/question-views/components/data-run/Field'

import style from '@aytm/app/stats/page/question-views/components/data-run/styles/QuestionState.module.scss'

// What the block shows in place of a chart. The slot keeps the page's own surface: this is the same
// report, briefly holding less, so nothing here is inverted or enlarged to mark it as exceptional.
const QuestionState = ({ type, cause, action, missing, colorTheme, jobProgress = 0, canRun = true, onRun }) => {
  const copy = calcCopy({ cause, type, missing })
  if (!copy) return null

  // Progress is latched. Polls arrive every fifteen seconds and several branches of the reply carry
  // no percentage at all, so reading the current value would drop a bar that was already at 62%
  // back to an indeterminate sweep and then restore it.
  const best = useRef(0)
  useEffect(() => {
    if (CAUSE.calculating !== cause) best.current = 0
    else if (jobProgress > best.current) best.current = jobProgress
  }, [cause, jobProgress])
  const reached = Math.max(jobProgress, CAUSE.calculating === cause ? best.current : 0)

  const mark = markFor(cause)
  const offersRun = REMEDY.run === remedyFor(cause) && canRun && 'function' == typeof onRun
  const running = CAUSE.calculating === cause

  // Progress is claimed only when it is known: several transports report nothing for a whole run,
  // so a determinate bar waits for a figure rather than inventing one.
  const known = running && 0 < reached

  return (
    <div className={style.state}>
      {mark ? <Field population={mark.population} still={mark.still} colorTheme={colorTheme} /> : null}

      <div className={style.say}>
        <b>{copy.title}</b>
        <span>{copy.detail}</span>
      </div>

      {offersRun ? (
        <Button
          data-testid="stat-question-state-run"
          className="p-button-rounded p-button-blue"
          label={RUN_LABEL}
          onClick={onRun}
        />
      ) : action ? (
        <Button
          data-testid="stat-question-state-action"
          className="p-button-rounded p-button-blue"
          label={action.label}
          disabled={action.disabled}
          onClick={action.onClick}
        />
      ) : null}

      {running ? (
        <div
          className={known ? style.rail : `${style.rail} ${style['rail-unknown']}`}
          role="progressbar"
          aria-label={RUNNING_LABEL}
          {...(known && { 'aria-valuenow': Math.round(reached), 'aria-valuemin': 0, 'aria-valuemax': 100 })}
          // the same share as a test hook, so a test reads it without leaning on the accessibility tree
          {...(known && { 'data-valuenow': Math.round(reached), 'data-valuemin': 0, 'data-valuemax': 100 })}
        >
          <i style={known ? { width: `${Math.min(100, reached)}%` } : undefined} />
        </div>
      ) : null}
    </div>
  )
}

QuestionState.displayName = 'QuestionState'

QuestionState.propTypes = {
  type: PropTypes.string,
  cause: PropTypes.oneOf(Object.values(CAUSE)).isRequired,
  // a remedy that is neither waiting nor running -- a filter the reader set and can clear
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    disabled: PropTypes.bool
  }),
  missing: PropTypes.string,
  colorTheme: PropTypes.array,
  jobProgress: PropTypes.number,
  canRun: PropTypes.bool,
  onRun: PropTypes.func
}

export default QuestionState
